/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import { useFlag } from '@reflag/react-sdk';
import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithStorage } from 'jotai/utils';
import React, { memo } from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import type {
    NavigationScreenContentGapsState,
    NavigationScreenLinkAuditState,
    NavigationScreenSiteState,
    NavigationScreenState,
    NavigationScreenTriageState,
} from '@gitbook/app-navigation';
import { createPalette, PaletteButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    DialogRoot,
    DialogTrigger,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    ScrollFader,
    Sidebar,
    SidebarActionButton,
    SidebarBody,
    SidebarButton,
    SidebarDisclosureMenu,
    SidebarFooter,
    SidebarHeader,
    SidebarHeading,
    SidebarItemList,
    SidebarItemSkeleton,
    SidebarLink,
    type SidebarLinkProps,
    SidebarToggleButton,
    Status,
    TextInput,
    Tooltip,
    useScrollFaderController,
} from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import {
    APIErrorBoundary,
    AppVersionAlert,
    CreateSiteDialog,
    GitSyncTargetStatusProvider,
    getGitSyncStatusPresentation,
    getSiteCustomizationTabSiteSpaceId,
    getSiteGitSyncSetupTarget,
    useNavigableSiteNewAnalyticsTabs,
    hasGitSyncInstallation,
    isFailedGitSyncStatus,
    NewFeatureBadge,
    NotificationsBell,
    OrganizationSearchButton,
    SiteContentIcon,
    SiteSortBy,
    siteCustomizationTabs,
    useGitSyncCurrentStatus,
    useSiteNewAnalyticsTrialingPlans,
    useSortSites,
} from '../../Components';
import { CreateRootSiteItemButton } from '../../Components/Site/SiteStructure/CreateRootSiteItemButton';
import { confirmDetachStyleguide } from '../../Components/Styleguide';
import { useNavigation } from '../../navigation';
import { useVisibleSiteSettingsSections } from '../../Screens/Site/components/Settings';
import {
    apiQuery,
    desktopSidebarCollapsedAtom,
    SiteContextProvider,
    type SiteCapability,
    type UseUpgradeSite,
    useIsSidebarLocked,
    useTrialingOrganizationFeaturePlan,
    useTrialingSiteFeaturePlan,
    useUnlockSidebar,
    useUpgradeSite,
} from '../../state';
import { createDocumentationURL } from '../../utils';
import { SidebarActionsButton } from '../SidebarActionsButton';
import { SidebarTrialingBadge } from '../SidebarTrialingBadge';
import {
    isOrgLevelScreen,
    shouldShowOrgContentSidebar,
    SIDEBAR_VISIBLE_ITEMS_DEFAULT,
} from './ContentSidebar.utils';
import { CustomerIOAlert } from './CustomerIOAlert';
import { FeedbackSidebarButton } from './FeedbackSidebarButton';
import { HelpButton } from './HelpButton';
import {
    CollapsedOrganizationTrialAlertButton,
    CollapsibleOrganizationAlert,
    OrganizationAlert,
} from './OrganizationAlert';
import { OrganizationButton } from './OrganizationButton';
import {
    type IsSidebarSectionExpanded,
    SIDEBAR_SECTION,
    type SetSidebarSectionExpanded,
    type SidebarSection,
    useSidebarSectionsExpanded,
} from './sidebarSections';
import { SidebarSiteTree, useCollapseAllSiteItems } from './Sites';
import { SiteSwitcherButton } from './SiteSwitcherButton';
import { CreateContentButton, SpacesTree } from './Spaces';
import { useAutoExpandCollections } from './useAutoExpandCollections';

export interface ContentSidebarProps {
    screen: NavigationScreenState;
}

/**
 * Content sidebar.
 */
export function ContentSidebar(props: ContentSidebarProps) {
    return (
        <Sidebar>
            <ContentSidebarContent {...props} />
        </Sidebar>
    );
}

function ContentSidebarContent(props: ContentSidebarProps) {
    const { screen } = props;

    if (!('organization' in screen) || !screen.organization) {
        return <NoOrganizationEmptyState />;
    }

    return (
        <APIErrorBoundary
            fallback={{
                404: <NoOrganizationEmptyState />,
                403: <NoOrganizationEmptyState />,
            }}
        >
            <ContentSidebarContentWithOrg screen={screen} organizationId={screen.organization} />
        </APIErrorBoundary>
    );
}

function NoOrganizationEmptyState() {
    return (
        <EmptyState>
            <EmptyStateIcon>
                <Icon.Organization />
            </EmptyStateIcon>
            <Description>
                Join an organization or create a new one to get started with your content.
            </Description>
            <EmptyStateActions>
                <Button
                    kind="secondary"
                    label="Create organization"
                    href={{ screen: { type: 'welcome' }, overlay: null }}
                />
            </EmptyStateActions>
        </EmptyState>
    );
}

/**
 * Memoized content of the sidebar, optimized to re-render only when relevant url parts change.
 */
function ContentSidebarContentWithOrg(props: {
    organizationId: string;
    screen: NavigationScreenState;
}) {
    const { organizationId, screen } = props;
    const siteId = 'site' in screen ? screen.site : undefined;
    const scrollFader = useScrollFaderController('vertical');

    const [{ data: organization }, { data: orgAllContent }] = useSuspenseQueries({
        queries: [
            apiQuery('getOrganizationById', { organizationId }),
            apiQuery('getOrganizationAllContent', { organizationId }),
        ],
    });
    const siteContent = siteId
        ? orgAllContent.sites.find((item) => item.site.id === siteId)
        : undefined;
    const site = siteContent?.site ?? null;
    const structure = siteContent?.structure ?? null;
    useAutoExpandCollections({ orgAllContent, screen });

    return (
        <>
            <ContentSidebarHeader
                organization={organization}
                organizationContent={orgAllContent}
                screen={screen}
                site={site}
            />

            <ScrollFader
                controller={scrollFader}
                kind="sidebar"
                className="flex h-auto! min-h-0 flex-1 flex-col before:to-[8%] after:to-[8%]"
            >
                <SidebarBody
                    ref={scrollFader.ref}
                    fullWidth
                    aria-label="Main navigation"
                    className="min-h-0 scroll-pb-12 pb-10 [scrollbar-gutter:stable]"
                >
                    {shouldShowOrgContentSidebar(screen, orgAllContent) ? (
                        <OrgContentSidebar
                            content={orgAllContent}
                            organization={organization}
                            screen={screen}
                        />
                    ) : (
                        <SiteContentSidebar
                            site={site}
                            structure={structure}
                            organization={organization}
                            orgAllContent={orgAllContent}
                            screen={screen}
                        />
                    )}
                </SidebarBody>
            </ScrollFader>

            <ContentSidebarFooter
                organization={organization}
                screen={screen}
                showTrash={orgAllContent.deletedSpaces.length > 0}
            />
        </>
    );
}

function OrgContentSidebar(props: {
    content: api.OrganizationAllContent;
    organization: api.Organization;
    screen: NavigationScreenState;
}) {
    const {
        content,
        screen,
        organization: { id: organizationId, permissions },
    } = props;
    const [isSidebarSectionExpanded, setSidebarSectionExpanded] = useSidebarSectionsExpanded();
    // A style guide opened at the org level (no site context) shows the list of the
    // organization's style guides instead of the regular spaces tree.
    const isStyleguideSpace =
        screen.type === 'space' &&
        !screen.site &&
        content.sites.some((item) => item.site.styleguide?.space === screen.space);

    return (
        <div className="flex flex-col gap-4">
            <SidebarItemList fullWidth>
                <HomeLink screen={screen} />
                <SitesLink label="Docs sites" screen={screen} />
                <ContentScreenLink screen={screen} />
                {permissions.listMembers ? (
                    <OpenAPISettingsLink organizationId={organizationId} />
                ) : null}
                {permissions.listMembers ? (
                    <TranslationsSettingsLink organizationId={organizationId} />
                ) : null}
                {permissions.listIntegrations ? (
                    <IntegrationsSettingsLink organizationId={organizationId} />
                ) : null}
                <McpSettingsLink />
            </SidebarItemList>

            {permissions.admin ? (
                <SidebarDisclosureMenu
                    kind="section"
                    label="Organization"
                    isExpanded={isSidebarSectionExpanded(SIDEBAR_SECTION.organization)}
                    onExpandedChange={(isExpanded) => {
                        setSidebarSectionExpanded(SIDEBAR_SECTION.organization, isExpanded);
                    }}
                    toggleVisibility="active"
                    className="pr-1.5"
                >
                    <OrganizationSettingsLink organizationId={organizationId} />
                    <MembersLink screen={screen} organizationId={organizationId} />
                    <BillingLink screen={screen} organizationId={organizationId} />
                </SidebarDisclosureMenu>
            ) : null}

            <DocsSitesSection
                key={organizationId}
                content={content}
                isSidebarSectionExpanded={isSidebarSectionExpanded}
                organization={props.organization}
                setSidebarSectionExpanded={setSidebarSectionExpanded}
            />

            <SidebarDisclosureMenu
                kind="section"
                label={isStyleguideSpace ? 'Style guides' : 'Spaces'}
                isExpanded={isSidebarSectionExpanded(SIDEBAR_SECTION.spaces)}
                onExpandedChange={(isExpanded) => {
                    setSidebarSectionExpanded(SIDEBAR_SECTION.spaces, isExpanded);
                }}
                toggleVisibility="active"
                className="pr-1.5"
                trailing={
                    !isStyleguideSpace && permissions.createContent ? (
                        <CreateContentButton organizationId={organizationId} kind="sidebar" />
                    ) : null
                }
            >
                {isStyleguideSpace ? (
                    <React.Suspense fallback={<SidebarItemSkeleton />}>
                        <StyleguidesContentList organizationId={organizationId} screen={screen} />
                    </React.Suspense>
                ) : (
                    <SpacesTree
                        key={organizationId}
                        organization={props.organization}
                        content={content}
                        screen={screen}
                    />
                )}
            </SidebarDisclosureMenu>
        </div>
    );
}

function DocsSitesSection(props: {
    content: api.OrganizationAllContent;
    isSidebarSectionExpanded: IsSidebarSectionExpanded;
    organization: api.Organization;
    setSidebarSectionExpanded: SetSidebarSectionExpanded;
}) {
    const { content, isSidebarSectionExpanded, organization, setSidebarSectionExpanded } = props;

    const { sortedSites } = useSortSites({
        allSites: content.sites,
        sortBy: SiteSortBy.Viewed,
        organizationId: organization.id,
    });
    const allSites = sortedSites.map(({ item }) => item);
    const [showAllSites, setShowAllSites] = React.useState(false);
    const visibleSites = showAllSites ? allSites : allSites.slice(0, SIDEBAR_VISIBLE_ITEMS_DEFAULT);

    return (
        <SidebarDisclosureMenu
            kind="section"
            label="Docs sites"
            isExpanded={isSidebarSectionExpanded(SIDEBAR_SECTION.sites)}
            onExpandedChange={(isExpanded) => {
                setSidebarSectionExpanded(SIDEBAR_SECTION.sites, isExpanded);
            }}
            toggleVisibility="active"
            className="pr-1.5"
            trailing={
                organization.permissions.createContent ? (
                    <DialogRoot>
                        <DialogTrigger>
                            <SidebarActionButton
                                label="Create a site"
                                icon={Icon.Plus}
                                className="group-hover/sidebar:opacity-100 group-has-focus-visible/sidebar:opacity-100"
                            />
                        </DialogTrigger>
                        <CreateSiteDialog organizationId={organization.id} />
                    </DialogRoot>
                ) : null
            }
        >
            {visibleSites.map(({ site }) => (
                <SidebarLink
                    key={site.id}
                    label={site.title}
                    icon={<SiteContentIcon site={site} />}
                    href={{
                        screen: {
                            type: 'site',
                            organization: organization.id,
                            site: site.id,
                        },
                    }}
                />
            ))}
            {allSites.length > SIDEBAR_VISIBLE_ITEMS_DEFAULT ? (
                <SidebarButton
                    label={showAllSites ? 'Show less' : 'Show all'}
                    icon={showAllSites ? Icon.Minus : Icon.MoreHorizontal}
                    onPress={() => setShowAllSites((showAll) => !showAll)}
                />
            ) : null}
        </SidebarDisclosureMenu>
    );
}

function SiteContentSidebar(props: {
    site: api.Site | null;
    structure: api.SiteStructure | null;
    organization: api.Organization;
    orgAllContent: api.OrganizationAllContent;
    screen: NavigationScreenState;
}) {
    const { site, structure, ...rest } = props;

    if (!site) {
        // This state can happen when a screen triggered a redirection
        // so we show a loading state in the sidebar while we fetch the data.
        return <ContentSidebarSkeleton />;
    }

    if (!structure) {
        return null;
    }

    return (
        <SiteContextProvider site={site} organizationId={props.organization.id}>
            <SiteContentSidebarWithStructure site={site} structure={structure} {...rest} />
        </SiteContextProvider>
    );
}

function SiteContentSidebarWithStructure(props: {
    site: api.Site;
    structure: api.SiteStructure;
    organization: api.Organization;
    orgAllContent: api.OrganizationAllContent;
    screen: NavigationScreenState;
}) {
    const { organization, screen, site, structure } = props;
    const { isEnabled: isTriageEnabled } = useFlag('TRIAGE');
    const isNativeGitSyncSitesFlagEnabled = useFlag('NATIVE_GIT_SYNC_SITES').isEnabled;
    const isUpdatedAnalyticsEnabled = useFlag('UPDATED_ANALYTICS_SCREEN').isEnabled;
    const isLinkAuditEnabled = useFlag('LINK_AUDIT').isEnabled;
    const newAnalyticsTrialingPlans = useSiteNewAnalyticsTrialingPlans(site);
    const navigableAnalyticsTabs = useNavigableSiteNewAnalyticsTabs(site);

    const sitePermissions = React.useMemo<SiteCapability>(
        () => ({
            canAdminSite: site.permissions.admin,
            canEditSite: site.permissions.edit,
            canCreateSite: site.permissions.create,
            canViewSite: site.permissions.access,
            installIntegration: site.permissions.installIntegration,
        }),
        [site]
    );
    const visibleSettingsSections = useVisibleSiteSettingsSections(site, organization);
    const hasStyleguideLink = hasSiteStyleguideLink(site, sitePermissions.canAdminSite);
    const upgrade = useUpgradeSite(site, api.SiteFeatureKey.SitesSections);
    const {
        trialingSectionsPlan,
        trialingRedirectsPlan,
        trialingAiInsightsPlan,
        trialingConnectionsPlan,
        trialingChannelsPlan,
    } = useSiteNavigationTrialingPlans(site);
    const [isSidebarSectionExpanded, setSidebarSectionExpanded] = useSidebarSectionsExpanded();

    const currentSiteView = screen.type === 'site' ? screen.tab : undefined;
    const getSiteScreenHref = (
        tab: SiteView,
        sideSheet?: NavigationScreenSiteState['sideSheet']
    ): SidebarLinkProps['href'] => ({
        screen: {
            type: 'site',
            organization: organization.id,
            site: site.id,
            tab,
            sideSheet,
        },
    });

    const {
        canViewSiteTools,
        expandedMenu,
        showCustomizeMenu,
        showExtendMenu,
        showSettingsMenu,
        setExpandedMenu,
    } = useSiteToolsExpandableMenus({
        currentSiteView,
        isSidebarSectionExpanded,
        screen,
        setSidebarSectionExpanded,
        sitePermissions,
        visibleSettingsSections,
    });

    // Keeps the visibility the link had while it lived under Tools.
    const showStyleguideLink = canViewSiteTools && hasStyleguideLink;

    return (
        <nav aria-label="Site navigation" className="flex w-full flex-none flex-col gap-4">
            <SidebarDisclosureMenu
                kind="section"
                label="General"
                isExpanded={isSidebarSectionExpanded(SIDEBAR_SECTION.general)}
                onExpandedChange={(isExpanded) => {
                    setSidebarSectionExpanded(SIDEBAR_SECTION.general, isExpanded);
                }}
                toggleVisibility="active"
                className="pr-1.5"
            >
                <OverviewLink screen={screen} />
                <ChangeRequestsLink screen={screen} />
                {isTriageEnabled ? (
                    <SiteTriageLink
                        organizationId={organization.id}
                        siteId={site.id}
                        screen={screen}
                    />
                ) : null}
                {sitePermissions.canEditSite ? (
                    <StructureLink screen={screen} trialingPlan={trialingSectionsPlan} />
                ) : null}
                {showSettingsMenu ? (
                    <SidebarDisclosureMenu
                        label="Settings"
                        icon={Icon.Settings}
                        isExpanded={expandedMenu === 'settings'}
                        onExpandedChange={(expanded) => {
                            setExpandedMenu('settings', expanded);
                        }}
                    >
                        {visibleSettingsSections.general ? (
                            <SidebarLink
                                label="General"
                                icon={Icon.Options02}
                                isActive={
                                    currentSiteView?.type === 'settings' &&
                                    currentSiteView.section === 'general'
                                }
                                href={getSiteScreenHref({
                                    type: 'settings',
                                    section: 'general',
                                })}
                            />
                        ) : null}
                        {sitePermissions.canAdminSite ? <SiteMembersLink screen={screen} /> : null}
                        {visibleSettingsSections.agent ? (
                            <SidebarLink
                                label="Agents"
                                icon={Icon.AIAgent}
                                isActive={
                                    currentSiteView?.type === 'settings' &&
                                    currentSiteView.section === 'agent'
                                }
                                href={getSiteScreenHref({
                                    type: 'settings',
                                    section: 'agent',
                                })}
                            />
                        ) : null}
                        {visibleSettingsSections.audience ? (
                            <SidebarLink
                                label="Audience"
                                icon={Icon.PublishStatusPublic}
                                isActive={
                                    currentSiteView?.type === 'settings' &&
                                    (currentSiteView.section === 'audience' ||
                                        currentSiteView.section === 'schema')
                                }
                                href={getSiteScreenHref({
                                    type: 'settings',
                                    section: 'audience',
                                })}
                            />
                        ) : null}
                        {visibleSettingsSections.domains ? (
                            <SidebarLink
                                label="Domain and URL"
                                icon={Icon.Globe}
                                isActive={
                                    currentSiteView?.type === 'settings' &&
                                    currentSiteView.section === 'domains'
                                }
                                href={getSiteScreenHref({
                                    type: 'settings',
                                    section: 'domains',
                                })}
                            />
                        ) : null}
                        {visibleSettingsSections.redirects ? (
                            <SidebarLink
                                label="Redirects"
                                icon={Icon.Redirect}
                                isActive={
                                    currentSiteView?.type === 'settings' &&
                                    currentSiteView.section === 'redirects'
                                }
                                href={getSiteScreenHref({
                                    type: 'settings',
                                    section: 'redirects',
                                })}
                            >
                                <SidebarTrialingBadge plan={trialingRedirectsPlan} />
                            </SidebarLink>
                        ) : null}
                        {visibleSettingsSections.sponsorship ? (
                            <SidebarLink
                                label="Ads and sponsorship"
                                icon={Icon.PaidMember}
                                isActive={
                                    currentSiteView?.type === 'settings' &&
                                    currentSiteView.section === 'sponsorship'
                                }
                                href={getSiteScreenHref({
                                    type: 'settings',
                                    section: 'sponsorship',
                                })}
                            />
                        ) : null}
                        {visibleSettingsSections.plan ? (
                            <SidebarLink
                                label="Plan"
                                icon={Icon.Upgrade}
                                isActive={
                                    currentSiteView?.type === 'settings' &&
                                    currentSiteView.section === 'plan'
                                }
                                href={getSiteScreenHref({
                                    type: 'settings',
                                    section: 'plan',
                                })}
                            />
                        ) : null}
                    </SidebarDisclosureMenu>
                ) : null}
                {/* Up for discussion: <NotificationsBell label="Inbox" icon={Icon.Files} showCount /> */}
            </SidebarDisclosureMenu>

            {/* Quality */}
            <SidebarDisclosureMenu
                kind="section"
                label="Quality"
                isExpanded={isSidebarSectionExpanded(SIDEBAR_SECTION.quality)}
                onExpandedChange={(isExpanded) => {
                    setSidebarSectionExpanded(SIDEBAR_SECTION.quality, isExpanded);
                }}
                toggleVisibility="active"
                className="pr-1.5"
            >
                {showStyleguideLink ? (
                    <SiteStyleguideLink
                        site={site}
                        organizationId={organization.id}
                        screen={screen}
                        canAdminSite={sitePermissions.canAdminSite}
                    />
                ) : null}

                <SiteContentGapsLink
                    organizationId={organization.id}
                    siteId={site.id}
                    screen={screen}
                />

                {isLinkAuditEnabled ? (
                    <SiteLinkAuditLink
                        organizationId={organization.id}
                        siteId={site.id}
                        screen={screen}
                    />
                ) : null}
            </SidebarDisclosureMenu>

            {/* Tools */}
            {canViewSiteTools ? (
                <SidebarDisclosureMenu
                    kind="section"
                    label="Tools"
                    isExpanded={isSidebarSectionExpanded(SIDEBAR_SECTION.tools)}
                    onExpandedChange={(isExpanded) => {
                        setSidebarSectionExpanded(SIDEBAR_SECTION.tools, isExpanded);
                    }}
                    toggleVisibility="active"
                    className="pr-1.5"
                >
                    {sitePermissions.canAdminSite && isNativeGitSyncSitesFlagEnabled ? (
                        <GitSyncSidebarLink
                            site={site}
                            organizationId={organization.id}
                            screen={screen}
                        />
                    ) : null}

                    {showCustomizeMenu ? (
                        <SidebarDisclosureMenu
                            label="Customize"
                            icon={Icon.Customization}
                            isExpanded={expandedMenu === 'customize'}
                            onExpandedChange={(expanded) => {
                                setExpandedMenu('customize', expanded);
                            }}
                        >
                            {sitePermissions.canAdminSite
                                ? siteCustomizationTabs.map((tab) => (
                                      <SidebarLink
                                          key={tab.id}
                                          label={tab.label}
                                          icon={tab.icon}
                                          isActive={
                                              currentSiteView?.type === 'customization' &&
                                              currentSiteView.tab === tab.id
                                          }
                                          href={getSiteScreenHref({
                                              type: 'customization',
                                              tab: tab.id,
                                              siteSpaceId: getSiteCustomizationTabSiteSpaceId(
                                                  tab.id,
                                                  currentSiteView?.type === 'customization'
                                                      ? currentSiteView.siteSpaceId
                                                      : undefined
                                              ),
                                          })}
                                      />
                                  ))
                                : null}
                        </SidebarDisclosureMenu>
                    ) : null}

                    <SidebarDisclosureMenu
                        label="Analyze"
                        icon={Icon.Insights}
                        isExpanded={expandedMenu === 'analyze'}
                        onExpandedChange={(expanded) => {
                            setExpandedMenu('analyze', expanded);
                        }}
                    >
                        {isUpdatedAnalyticsEnabled ? (
                            navigableAnalyticsTabs.map((tab) => (
                                <SidebarLink
                                    key={tab.id}
                                    label={tab.label}
                                    icon={tab.icon}
                                    isActive={
                                        currentSiteView?.type === 'analytics' &&
                                        currentSiteView.report === tab.id
                                    }
                                    href={getSiteScreenHref({
                                        type: 'analytics',
                                        report: tab.id,
                                    })}
                                >
                                    <SidebarTrialingBadge
                                        plan={newAnalyticsTrialingPlans[tab.id] ?? null}
                                    />
                                </SidebarLink>
                            ))
                        ) : (
                            <>
                                <SidebarLink
                                    label="AI Insights"
                                    icon={Icon.Insights}
                                    isActive={currentSiteView?.type === 'insights'}
                                    href={getSiteScreenHref({
                                        type: 'insights',
                                        view: { type: 'home' },
                                    })}
                                >
                                    <SidebarTrialingBadge plan={trialingAiInsightsPlan} />
                                </SidebarLink>
                                <SidebarLink
                                    label="Analytics"
                                    icon={Icon.MaximumValue}
                                    isActive={currentSiteView?.type === 'analytics'}
                                    href={getSiteScreenHref({
                                        type: 'analytics',
                                        report: 'traffic',
                                    })}
                                />
                            </>
                        )}
                    </SidebarDisclosureMenu>

                    {showExtendMenu ? (
                        <SidebarDisclosureMenu
                            label="Extend"
                            icon={Icon.Extend}
                            isExpanded={expandedMenu === 'extend'}
                            onExpandedChange={(expanded) => {
                                setExpandedMenu('extend', expanded);
                            }}
                        >
                            {visibleSettingsSections.connections ? (
                                <SidebarLink
                                    label="Connections"
                                    icon={Icon.Connectors}
                                    isActive={
                                        currentSiteView?.type === 'settings' &&
                                        currentSiteView.section === 'connections'
                                    }
                                    href={getSiteScreenHref({
                                        type: 'settings',
                                        section: 'connections',
                                    })}
                                >
                                    <SidebarTrialingBadge plan={trialingConnectionsPlan} />
                                </SidebarLink>
                            ) : null}
                            {visibleSettingsSections.channels ? (
                                <SidebarLink
                                    label="Channels"
                                    icon={Icon.Channel}
                                    isActive={
                                        currentSiteView?.type === 'settings' &&
                                        currentSiteView.section === 'channels'
                                    }
                                    href={getSiteScreenHref({
                                        type: 'settings',
                                        section: 'channels',
                                    })}
                                >
                                    <SidebarTrialingBadge plan={trialingChannelsPlan} />
                                </SidebarLink>
                            ) : null}
                            {visibleSettingsSections.embed ? (
                                <SidebarLink
                                    label="Docs Embed"
                                    icon={Icon.DocsEmbed}
                                    isActive={
                                        currentSiteView?.type === 'settings' &&
                                        currentSiteView.section === 'embed'
                                    }
                                    href={getSiteScreenHref({
                                        type: 'settings',
                                        section: 'embed',
                                    })}
                                />
                            ) : null}
                            {visibleSettingsSections.mcp ? (
                                <SidebarLink
                                    label="MCP access"
                                    icon={Icon.MCP}
                                    isActive={
                                        currentSiteView?.type === 'settings' &&
                                        currentSiteView.section === 'mcp'
                                    }
                                    href={getSiteScreenHref({
                                        type: 'settings',
                                        section: 'mcp',
                                    })}
                                />
                            ) : null}
                            <SidebarLink
                                label="Integrations"
                                icon={Icon.Integrations}
                                isActive={
                                    screen.type === 'site' &&
                                    screen.sideSheet?.type === 'integrations'
                                }
                                href={getSiteScreenHref(currentSiteView, {
                                    type: 'integrations',
                                })}
                            />
                        </SidebarDisclosureMenu>
                    ) : null}
                </SidebarDisclosureMenu>
            ) : null}

            <SiteContentSidebarSiteStructure
                organization={organization}
                screen={screen}
                site={site}
                sitePermissions={sitePermissions}
                structure={structure}
                upgrade={upgrade}
            />
        </nav>
    );
}

function useSiteNavigationTrialingPlans(site: api.Site) {
    const trialingSectionsPlan = useTrialingSiteFeaturePlan(site, api.SiteFeatureKey.SitesSections);
    const trialingRedirectsPlan = useTrialingSiteFeaturePlan(
        site,
        api.SiteFeatureKey.SitesRedirects
    );
    const trialingAiInsightsPlan = useTrialingSiteFeaturePlan(
        site,
        api.SiteFeatureKey.SitesAiInsights
    );
    const trialingConnectionsPlan = useTrialingSiteFeaturePlan(
        site,
        api.SiteFeatureKey.SitesConnections
    );
    const trialingChannelsPlan = useTrialingSiteFeaturePlan(site, api.SiteFeatureKey.SitesChannels);

    return {
        trialingSectionsPlan,
        trialingRedirectsPlan,
        trialingAiInsightsPlan,
        trialingConnectionsPlan,
        trialingChannelsPlan,
    };
}

const organizationTrialAlertCollapsedAtom = atomFamily((organizationId: string) =>
    atomWithStorage(
        `organizationTrialAlertCollapsed__${JSON.stringify(organizationId)}`,
        false,
        undefined,
        { getOnInit: true }
    )
);

type SiteDisclosureMenuId = 'customize' | 'analyze' | 'extend' | 'settings';
type SiteView = NavigationScreenSiteState['tab'];

const SITE_DISCLOSURE_MENU_IDS: SiteDisclosureMenuId[] = [
    'customize',
    'analyze',
    'extend',
    'settings',
];

const SITE_DISCLOSURE_MENU_SECTIONS: Record<SiteDisclosureMenuId, SidebarSection> = {
    customize: SIDEBAR_SECTION.customize,
    analyze: SIDEBAR_SECTION.analyze,
    extend: SIDEBAR_SECTION.extend,
    settings: SIDEBAR_SECTION.settings,
};

function useSiteToolsExpandableMenus(props: {
    currentSiteView: SiteView | undefined;
    isSidebarSectionExpanded: IsSidebarSectionExpanded;
    screen: NavigationScreenState;
    setSidebarSectionExpanded: SetSidebarSectionExpanded;
    sitePermissions: SiteCapability;
    visibleSettingsSections: ReturnType<typeof useVisibleSiteSettingsSections>;
}) {
    const {
        currentSiteView,
        isSidebarSectionExpanded,
        screen,
        setSidebarSectionExpanded,
        sitePermissions,
        visibleSettingsSections,
    } = props;

    const canViewSiteTools = sitePermissions.canViewSite || sitePermissions.canAdminSite;
    const showCustomizeMenu = sitePermissions.canAdminSite;
    const showExtendMenu = sitePermissions.canAdminSite;
    const showSettingsMenu =
        sitePermissions.canAdminSite &&
        (visibleSettingsSections.general ||
            visibleSettingsSections.agent ||
            visibleSettingsSections.audience ||
            visibleSettingsSections.domains ||
            visibleSettingsSections.redirects ||
            visibleSettingsSections.sponsorship ||
            visibleSettingsSections.plan);

    const isCustomizeActive = currentSiteView?.type === 'customization';
    const isAnalyzeActive =
        currentSiteView?.type === 'analytics' || currentSiteView?.type === 'insights';
    const isExtendActive =
        currentSiteView?.type === 'settings' &&
        ((visibleSettingsSections.mcp && currentSiteView.section === 'mcp') ||
            (visibleSettingsSections.connections && currentSiteView.section === 'connections') ||
            (visibleSettingsSections.channels && currentSiteView.section === 'channels') ||
            (visibleSettingsSections.embed && currentSiteView.section === 'embed'));
    const isIntegrationsActive =
        screen.type === 'site' && screen.sideSheet?.type === 'integrations';
    const isSettingsActive =
        currentSiteView?.type === 'members' ||
        (currentSiteView?.type === 'settings' &&
            ((visibleSettingsSections.general && currentSiteView.section === 'general') ||
                (visibleSettingsSections.agent && currentSiteView.section === 'agent') ||
                (visibleSettingsSections.audience &&
                    (currentSiteView.section === 'audience' ||
                        currentSiteView.section === 'schema')) ||
                (visibleSettingsSections.domains && currentSiteView.section === 'domains') ||
                (visibleSettingsSections.redirects && currentSiteView.section === 'redirects') ||
                (visibleSettingsSections.sponsorship &&
                    currentSiteView.section === 'sponsorship') ||
                (visibleSettingsSections.plan && currentSiteView.section === 'plan')));

    const activeMenu: SiteDisclosureMenuId | null = canViewSiteTools
        ? showCustomizeMenu && isCustomizeActive
            ? 'customize'
            : isAnalyzeActive
              ? 'analyze'
              : showExtendMenu && (isExtendActive || isIntegrationsActive)
                ? 'extend'
                : showSettingsMenu && isSettingsActive
                  ? 'settings'
                  : null
        : null;

    const expandedMenu =
        SITE_DISCLOSURE_MENU_IDS.find((menu) =>
            isSidebarSectionExpanded(SITE_DISCLOSURE_MENU_SECTIONS[menu])
        ) ?? null;
    const setExpandedMenu = useEventCallback((menu: SiteDisclosureMenuId, expanded: boolean) => {
        setSidebarSectionExpanded(SITE_DISCLOSURE_MENU_SECTIONS[menu], expanded);
    });

    React.useEffect(() => {
        if (activeMenu) {
            setExpandedMenu(activeMenu, true);
        }
    }, [activeMenu, setExpandedMenu]);

    return {
        canViewSiteTools,
        expandedMenu,
        showCustomizeMenu,
        showExtendMenu,
        showSettingsMenu,
        setExpandedMenu,
    };
}

function SiteContentSidebarSiteStructure(props: {
    site: api.Site;
    structure: api.SiteStructure;
    organization: api.Organization;
    screen: NavigationScreenState;
    sitePermissions: SiteCapability;
    upgrade: UseUpgradeSite | null;
}) {
    const { organization, screen, site, sitePermissions, structure, upgrade } = props;

    const { hasExpandedItems, collapseAllItems } = useCollapseAllSiteItems();

    const [showSearch, setShowSearch] = React.useState(false);
    const [query, setQuery] = React.useState('');
    const isSearchActive = showSearch && query.trim().length > 0;

    return (
        <div className="flex min-h-52 flex-none flex-col gap-px">
            <div className="flex items-center justify-between px-3 pr-1.5">
                <SidebarHeading label="Content" />

                <div className="pointer-events-none flex items-center gap-1 opacity-0 transition-opacity group-hover/sidebar:pointer-events-auto group-hover/sidebar:opacity-100 group-has-[:focus-visible]/sidebar:pointer-events-auto group-has-[:focus-visible]/sidebar:opacity-100 group-has-[[aria-haspopup=true][aria-expanded=true]]/sidebar:pointer-events-auto group-has-[[aria-haspopup=true][aria-expanded=true]]/sidebar:opacity-100 has-[[aria-expanded=true]]:pointer-events-auto has-[[aria-expanded=true]]:opacity-100">
                    {!isSearchActive ? (
                        <Button
                            label="Collapse all"
                            leadingIcon={Icon.Collapse}
                            iconOnly
                            kind="blank"
                            size="small"
                            disabled={!hasExpandedItems}
                            aria-hidden={!hasExpandedItems}
                            onPress={collapseAllItems}
                            className={clsx(
                                hasExpandedItems
                                    ? 'opacity-1'
                                    : 'pointer-events-none opacity-0 transition-opacity'
                            )}
                        />
                    ) : null}

                    <Button
                        label={showSearch ? 'Hide site search' : 'Show site search'}
                        leadingIcon={Icon.Search}
                        iconOnly
                        kind="blank"
                        size="small"
                        onPress={() => {
                            if (showSearch) {
                                setQuery('');
                            }
                            setShowSearch((prev) => !prev);
                        }}
                    />

                    {sitePermissions.canEditSite ? (
                        <>
                            <Button
                                label="Manage site structure"
                                leadingIcon={Icon.Edit}
                                iconOnly
                                kind="blank"
                                size="small"
                                href={{
                                    screen: {
                                        type: 'site',
                                        organization: organization.id,
                                        site: site.id,
                                        tab: { type: 'structure' },
                                    },
                                }}
                            />

                            <CreateRootSiteItemButton
                                label="Add content"
                                organization={organization}
                                site={site}
                                siteStructure={structure}
                                upgrade={upgrade}
                                canAdminSite={sitePermissions.canAdminSite}
                                iconOnly
                                size="small"
                                kind="blank"
                            />
                        </>
                    ) : null}
                </div>
            </div>

            <div className="w-full overflow-x-hidden overflow-y-visible pr-1.5 pl-3">
                <div
                    aria-hidden={!showSearch}
                    className="grid transition-[grid-template-rows] duration-200 ease-out"
                    style={{ gridTemplateRows: showSearch ? '1fr' : '0fr' }}
                >
                    <div className="overflow-hidden px-1 pt-1">
                        <TextInput
                            autoFocus={showSearch}
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            onClear={() => setQuery('')}
                            leadingIcon={Icon.Search}
                            placeholder="Search site content"
                            className="mb-2 w-full"
                            disabled={!showSearch}
                        />
                    </div>
                </div>

                <SidebarSiteTree
                    siteContent={{ site, structure }}
                    organization={organization}
                    organizationId={organization.id}
                    screen={screen}
                    sitePermissions={sitePermissions}
                    upgrade={upgrade}
                    searchQuery={showSearch ? query : ''}
                />
            </div>
        </div>
    );
}

const ContentSidebarHeader = memo(function ContentSidebarHeader(props: {
    site: api.Site | null;
    screen: NavigationScreenState;
    organization: api.Organization;
    organizationContent: api.OrganizationAllContent;
}) {
    const { organization, organizationContent, screen, site } = props;

    const [isCollapsed, setCollapse] = useAtom(desktopSidebarCollapsedAtom);
    const isLocked = useIsSidebarLocked();
    const unlock = useUnlockSidebar();

    const siteId = 'site' in screen && screen.site;

    return (
        <SidebarHeader>
            <div className="flex min-w-0 flex-1 flex-col space-y-3">
                <div className="flex w-full min-w-0 items-center">
                    <div className="flex flex-1">
                        <OrganizationButton
                            organization={organization}
                            organizationContent={organizationContent}
                            screen={screen}
                            iconOnly={!isOrgLevelScreen(screen)}
                        />
                    </div>

                    <div className="flex items-center gap-0.5">
                        {siteId ? (
                            <Button
                                kind="blank"
                                iconOnly
                                leadingIcon={Icon.BackHome}
                                label="Back to home"
                                onPress={() => {
                                    analytics.logEvent({
                                        type: 'click',
                                        action: 'open',
                                        organization: organization.id,
                                        screen: screen.type,
                                        screenSection: 'sidebar_header',
                                        component: 'home_link',
                                    });
                                }}
                                href={{
                                    screen: {
                                        type: 'home',
                                        organization: organization.id,
                                    },
                                }}
                            />
                        ) : null}
                        <OrganizationSearchButton
                            data-testid="sidebar.findButton"
                            organization={organization}
                        />
                        <NotificationsBell iconOnly placement="bottom-start" />

                        {/* We only show the toggle button when the sidebar is closed.
                Collapsing it handled only by the sidebar resize bar. */}
                        {isCollapsed && (
                            <SidebarToggleButton
                                isCollapsed={isLocked || isCollapsed}
                                onCollapse={() => {
                                    setCollapse(true);
                                }}
                                onUncollapse={() => {
                                    unlock();
                                    setCollapse(false);
                                }}
                                outlined={false}
                            />
                        )}
                    </div>
                </div>

                {siteId && site ? (
                    <SiteSwitcherButton
                        screen={screen}
                        organization={organization}
                        canCreateSite={organization.permissions.createContent}
                        organizationContent={organizationContent}
                        site={site}
                    />
                ) : null}
            </div>
        </SidebarHeader>
    );
});

export const ContentSidebarFooter = memo(function ContentSidebarFooter(props: {
    organization: api.Organization;
    screen: NavigationScreenState;
    showTrash: boolean;
}) {
    const { organization, screen, showTrash } = props;
    const [isTrialAlertCollapsed, setIsTrialAlertCollapsed] = useAtom(
        organizationTrialAlertCollapsedAtom(organization.id)
    );
    const hasCollapsibleTrialAlert =
        organization.trial.status === api.BillingTrialStatus.Active &&
        Boolean(organization.trial.endDate);

    return (
        <>
            <SidebarFooter className="mb-2 pb-8 [&:has(>#customerio-alert:only-child:empty)]:hidden">
                {hasCollapsibleTrialAlert ? (
                    <CollapsibleOrganizationAlert
                        key={organization.id}
                        organization={organization}
                        collapsed={isTrialAlertCollapsed}
                        onCollapse={() => {
                            setIsTrialAlertCollapsed(true);
                        }}
                    />
                ) : (
                    <OrganizationAlert data-org-alerts organization={organization} />
                )}
                <CustomerIOAlert />
                <AppVersionAlert />
            </SidebarFooter>

            <div className="absolute inset-x-2 bottom-2 z-[2] flex items-center gap-1 bg-gradient-to-t from-(--sp-color-bg-side) from-30% to-transparent">
                <DocsButton organization={organization} screen={screen} />
                <HelpButton />
                <FeedbackSidebarButton label="Share feedback" outlined iconOnly />
                <TrashSidebarItem
                    organization={organization}
                    screen={screen}
                    showTrash={showTrash}
                />
                <CollapsedOrganizationTrialAlertButton
                    visible={hasCollapsibleTrialAlert && isTrialAlertCollapsed}
                    onPress={() => {
                        setIsTrialAlertCollapsed(false);
                    }}
                />
            </div>
        </>
    );
});

function TrashSidebarItem(props: {
    organization: api.Organization;
    screen: NavigationScreenState;
    showTrash: boolean;
}) {
    const { organization, screen, showTrash } = props;

    if (!showTrash || !organization.permissions.createContent) {
        return null;
    }

    return (
        <SidebarLink
            label="Trash"
            icon={Icon.Trashcan}
            iconOnly
            outlined
            rounded
            isActive={screen.type === 'trash'}
            href={{
                screen: {
                    type: 'trash',
                    organization: organization.id,
                },
            }}
        />
    );
}

function DocsButton(props: { organization: api.Organization; screen: NavigationScreenState }) {
    const { organization, screen } = props;

    return (
        <PaletteButton
            label="Docs"
            leadingIcon={Icon.Documentation}
            iconOnly
            kind="secondary"
            rounded
            palette={() =>
                createPalette({
                    groups: [
                        {
                            key: 'docs',
                            commands: [
                                {
                                    key: 'docs.documentation',
                                    title: 'Documentation',
                                    icon: Icon.BookOpen,
                                    trigger: () => {
                                        analytics.logEvent({
                                            type: 'click',
                                            action: 'open',
                                            organization: organization.id,
                                            screen: screen.type,
                                            screenSection: 'sidebar_footer',
                                            component: 'link_documentation',
                                        });
                                        window.open(createDocumentationURL(''), '_blank');
                                    },
                                },
                                {
                                    key: 'docs.guides',
                                    title: 'Guides',
                                    icon: Icon.Documentation,
                                    trigger: () => {
                                        analytics.logEvent({
                                            type: 'click',
                                            action: 'open',
                                            organization: organization.id,
                                            screen: screen.type,
                                            screenSection: 'sidebar_footer',
                                            component: 'link_guides',
                                        });
                                        window.open(createDocumentationURL('guides'), '_blank');
                                    },
                                },
                            ],
                        },
                    ],
                })
            }
        />
    );
}

/**
 * Home sidebar links.
 */
export function HomeLink(props: { screen: NavigationScreenState }) {
    const { screen } = props;

    return (
        <SidebarLink
            label="Home"
            icon={Icon.Home}
            isActive={screen.type === 'home'}
            href={{
                screen: {
                    type: 'home',
                    organization: 'organization' in screen ? screen.organization : undefined,
                },
            }}
        />
    );
}

export function SitesLink(props: { screen: NavigationScreenState; label?: string }) {
    const { screen, label = 'Docs sites' } = props;

    return (
        <SidebarLink
            label={label}
            icon={Icon.DocsSite}
            isActive={screen.type === 'sites'}
            href={{
                screen: {
                    type: 'sites',
                    organization: 'organization' in screen ? screen.organization : undefined,
                },
            }}
        />
    );
}

function ContentScreenLink(props: { screen: NavigationScreenState }) {
    const { screen } = props;
    return (
        <Tooltip label="Find all your spaces here" position="right">
            <SidebarLink
                label="Content"
                icon={Icon.AllSpaces}
                isActive={screen.type === 'org_content'}
                href={{
                    screen: {
                        type: 'org_content',
                        organization: 'organization' in screen ? screen.organization : undefined,
                    },
                }}
            />
        </Tooltip>
    );
}

function OpenAPISettingsLink(props: { organizationId: string }) {
    return (
        <SidebarLink
            label="OpenAPI"
            icon={Icon.OpenAPI}
            href={{
                screen: {
                    type: 'org_settings',
                    organization: props.organizationId,
                    tab: { type: 'openapi' },
                },
            }}
        />
    );
}

function TranslationsSettingsLink(props: { organizationId: string }) {
    const trialingPlan = useTrialingOrganizationFeaturePlan(api.OrganizationFeature.Translations);

    return (
        <SidebarLink
            label="Translations"
            icon={Icon.AITranslate}
            href={{
                screen: {
                    type: 'org_settings',
                    organization: props.organizationId,
                    tab: { type: 'translations', view: 'overview' },
                },
            }}
        >
            <SidebarTrialingBadge plan={trialingPlan} />
        </SidebarLink>
    );
}

function IntegrationsSettingsLink(props: { organizationId: string }) {
    return (
        <SidebarLink
            label="Integrations"
            icon={Icon.Integrations}
            href={{
                screen: {
                    type: 'org_settings',
                    organization: props.organizationId,
                    tab: { type: 'integrations' },
                },
            }}
        />
    );
}

export function McpSettingsLink() {
    return (
        <SidebarLink
            label="MCP"
            icon={Icon.MCP}
            href={{
                screen: {
                    type: 'account',
                    tab: 'developer',
                    developerTab: 'mcp',
                },
            }}
        />
    );
}

/**
 * Sidebar list of the organization's style guides, shown in place of the spaces
 * tree when a style guide is opened at the org level.
 */
function StyleguidesContentList(props: { organizationId: string; screen: NavigationScreenState }) {
    const { organizationId, screen } = props;
    const { data } = useSuspenseQuery(apiQuery('listOrganizationStyleguides', { organizationId }));
    const currentSpace = screen.type === 'space' ? screen.space : undefined;

    return (
        <>
            {data.styleguides.map((styleguide) => (
                <SidebarLink
                    key={styleguide.space.id}
                    label={styleguide.space.title}
                    icon={Icon.BookOpen}
                    isActive={currentSpace === styleguide.space.id}
                    href={{
                        screen: {
                            type: 'space',
                            organization: organizationId,
                            space: styleguide.space.id,
                            page: '',
                        },
                    }}
                />
            ))}
        </>
    );
}

/** Whether the style guide link has anything to point at — creating one is admin-only. */
function hasSiteStyleguideLink(site: api.Site, canAdminSite: boolean): boolean {
    return Boolean(site.styleguide?.space) || canAdminSite;
}

/**
 * Site-level style guide link. Points to the `styleguide` space when the site already has one,
 * otherwise to the page where a style guide can be created or picked.
 */
function SiteStyleguideLink(props: {
    site: api.Site;
    organizationId: string;
    screen: NavigationScreenState;
    canAdminSite: boolean;
}) {
    const { site, organizationId, screen, canAdminSite } = props;
    const hasLink = hasSiteStyleguideLink(site, canAdminSite);
    const { navigate } = useNavigation();

    if (!hasLink) {
        return null;
    }

    const styleguideSpace = site.styleguide?.space;

    const link = (
        <SidebarLink
            label="Style guide"
            icon={Icon.BookOpen}
            isActive={
                styleguideSpace
                    ? screen.type === 'space' && screen.space === styleguideSpace
                    : screen.type === 'styleguide_new' && screen.site === site.id
            }
            href={
                styleguideSpace
                    ? {
                          screen: {
                              // Keep the URL scoped to the site the style guide belongs to.
                              type: 'space',
                              space: styleguideSpace,
                              organization: organizationId,
                              site: site.id,
                              page: '',
                          },
                      }
                    : {
                          screen: {
                              type: 'styleguide_new',
                              organization: organizationId,
                              site: site.id,
                          },
                      }
            }
        >
            {!styleguideSpace ? <span className="ml-auto text-muted">Set up</span> : null}
        </SidebarLink>
    );

    if (!styleguideSpace) {
        return link;
    }

    return (
        <div className="group/sidebar-item relative">
            {link}
            {canAdminSite ? (
                <div className="absolute inset-y-0 right-1 flex items-center">
                    <SidebarActionsButton
                        palette={() =>
                            createPalette({
                                groups: [
                                    {
                                        key: 'styleguide',
                                        commands: [
                                            {
                                                key: 'detach',
                                                title: 'Detach style guide',
                                                icon: Icon.LinkBroken,
                                                trigger: () =>
                                                    confirmDetachStyleguide({
                                                        organizationId,
                                                        siteId: site.id,
                                                        siteTitle: site.title,
                                                        navigate,
                                                    }),
                                            },
                                        ],
                                    },
                                ],
                            })
                        }
                    />
                </div>
            ) : null}
        </div>
    );
}

/**
 * Site sidebar links.
 */
function OverviewLink(props: { screen: NavigationScreenState }) {
    const { screen } = props;
    return (
        <SidebarLink
            label="Overview"
            icon={Icon.Grid}
            isActive={screen.type === 'site' && !screen.tab && !screen.sideSheet}
            href={{
                screen: {
                    type: 'site',
                    site: 'site' in screen ? screen.site : undefined,
                    tab: undefined,
                    sideSheet: undefined,
                },
            }}
        />
    );
}

function StructureLink(props: { screen: NavigationScreenState; trialingPlan: string | null }) {
    const { screen, trialingPlan } = props;
    return (
        <SidebarLink
            label="Site structure"
            icon={Icon.SiteStructure}
            isActive={screen.type === 'site' && screen.tab?.type === 'structure'}
            href={{
                screen: {
                    type: 'site',
                    site: 'site' in screen ? screen.site : undefined,
                    tab: { type: 'structure' },
                },
            }}
        >
            <SidebarTrialingBadge plan={trialingPlan} />
        </SidebarLink>
    );
}

function SiteMembersLink(props: { screen: NavigationScreenState }) {
    const { screen } = props;
    return (
        <SidebarLink
            label="Members"
            icon={Icon.Users}
            isActive={screen.type === 'site' && screen.tab?.type === 'members'}
            href={{
                screen: {
                    type: 'site',
                    site: 'site' in screen ? screen.site : undefined,
                    tab: { type: 'members' },
                },
            }}
        />
    );
}

export function ChangeRequestsLink(props: { screen: NavigationScreenState }) {
    const { screen } = props;
    return (
        <SidebarLink
            label="Change requests"
            icon={Icon.ChangeRequest}
            isActive={screen.type === 'change_requests'}
            href={{
                screen: {
                    type: 'change_requests',
                    organization: 'organization' in screen ? screen.organization : undefined,
                    site: 'site' in screen ? screen.site : undefined,
                },
            }}
        />
    );
}

function GitSyncSidebarLink(props: {
    site: api.Site;
    organizationId: string;
    screen: NavigationScreenState;
}) {
    return (
        <GitSyncTargetStatusProvider target={props.site}>
            <GitSyncSidebarLinkContent {...props} />
        </GitSyncTargetStatusProvider>
    );
}

function GitSyncSidebarLinkContent(props: {
    site: api.Site;
    organizationId: string;
    screen: NavigationScreenState;
}) {
    const { organizationId, screen, site } = props;
    const hasInstallation = hasGitSyncInstallation(site);
    const installationProvider = site.gitSync?.installationProvider;
    const { status: gitSyncStatus } = useGitSyncCurrentStatus(site);
    const isPending =
        site.gitSync?.installationStatus &&
        site.gitSync.installationStatus !== api.GitSyncInstallationStatus.Active;
    const presentation = getGitSyncStatusPresentation({
        gitSyncStatus,
        hasInstallation,
        isPending: Boolean(isPending),
    });

    // A failed sync opens the sync status view (matching the space behaviour) instead of the
    // configuration. This link only renders behind the native site flow, so no extra flag gate is
    // needed here.
    const isSyncFailed = isFailedGitSyncStatus(gitSyncStatus);
    const setupTarget = getSiteGitSyncSetupTarget(
        site,
        organizationId,
        isSyncFailed && site.gitSync?.installationId ? 'status' : 'setup'
    );

    // The row is tight, so it uses the one-word wording; the full label and its action live on the
    // header pill.
    const sidebarStatus = presentation.compactLabel ? (
        <Status
            kind={presentation.kind}
            label={presentation.compactLabel}
            size="small"
            loading={presentation.loading}
            detail={presentation.detail}
            detailTooltip
        />
    ) : presentation.actionLabel ? (
        <span className="text-muted">{presentation.actionLabel}</span>
    ) : null;

    return (
        <SidebarLink
            label="Git Sync"
            icon={installationProvider === api.GitSyncProvider.Gitlab ? Icon.GitLab : Icon.GitHub}
            isActive={screen.type === 'site' && screen.sideSheet?.type === 'gitsync'}
            tooltip={{
                position: 'right',
                label: gitSyncStatus ? undefined : presentation.detail,
            }}
            href={setupTarget}
        >
            {sidebarStatus ? <span className="ml-auto">{sidebarStatus}</span> : null}
        </SidebarLink>
    );
}

function MembersLink(props: { screen: NavigationScreenState; organizationId: string }) {
    const { organizationId } = props;
    return (
        <SidebarLink
            label="Members"
            icon={Icon.Users}
            href={{
                screen: {
                    type: 'org_settings',
                    organization: organizationId,
                    tab: { type: 'members' },
                },
            }}
        />
    );
}

function BillingLink(props: { screen: NavigationScreenState; organizationId: string }) {
    const { organizationId } = props;
    return (
        <SidebarLink
            label="Billing"
            icon={Icon.Upgrade}
            href={{
                screen: {
                    type: 'org_settings',
                    organization: organizationId,
                    tab: { type: 'plans' },
                },
            }}
        />
    );
}

function OrganizationSettingsLink(props: { organizationId: string }) {
    return (
        <SidebarLink
            label="Settings"
            icon={Icon.Settings}
            href={{
                screen: {
                    type: 'org_settings',
                    organization: props.organizationId,
                    tab: { type: 'profile' },
                },
            }}
        />
    );
}

function SiteTriageLink(props: {
    organizationId: string;
    siteId: string;
    screen: NavigationScreenState;
}) {
    const { organizationId, siteId, screen } = props;

    return (
        <SidebarLink
            label="Triage"
            icon={Icon.Findings}
            isActive={screen.type === 'triage' && screen.site === siteId}
            href={{
                screen: {
                    type: 'triage',
                    organization: organizationId,
                    site: siteId,
                    // Reset to the bare `/triage` findings view, clearing any selected
                    // finding, sub-view, scan, or filters carried over from the current screen.
                    finding: undefined,
                    view: undefined,
                    filters: undefined,
                    scan: undefined,
                    scanFilters: undefined,
                } satisfies NavigationScreenTriageState,
            }}
        >
            <span className="ml-auto">
                <NewFeatureBadge flagKey="TRIAGE" />
            </span>
        </SidebarLink>
    );
}

function SiteLinkAuditLink(props: {
    organizationId: string;
    siteId: string;
    screen: NavigationScreenState;
}) {
    const { organizationId, siteId, screen } = props;

    return (
        <SidebarLink
            label="Broken links"
            icon={Icon.LinkBroken}
            isActive={screen.type === 'link_audit' && screen.site === siteId}
            href={{
                screen: {
                    type: 'link_audit',
                    organization: organizationId,
                    site: siteId,
                    finding: undefined,
                    filters: undefined,
                    orderBy: undefined,
                } satisfies NavigationScreenLinkAuditState,
            }}
        >
            <span className="ml-auto">
                <NewFeatureBadge flagKey="LINK_AUDIT" />
            </span>
        </SidebarLink>
    );
}

function SiteContentGapsLink(props: {
    organizationId: string;
    siteId: string;
    screen: NavigationScreenState;
}) {
    const { organizationId, siteId, screen } = props;

    return (
        <SidebarLink
            label="Content gaps"
            icon={Icon.FindingContentGap}
            isActive={screen.type === 'content_gaps' && screen.site === siteId}
            href={{
                screen: {
                    type: 'content_gaps',
                    organization: organizationId,
                    site: siteId,
                    finding: undefined,
                    view: undefined,
                    filters: undefined,
                    orderBy: undefined,
                } satisfies NavigationScreenContentGapsState,
            }}
        >
            <span className="ml-auto">
                <NewFeatureBadge flagKey="SITE_CONTEXT_CHANGE_REQUEST" />
            </span>
        </SidebarLink>
    );
}

function ContentSidebarSkeleton() {
    return (
        <div className="flex flex-col gap-3">
            {/* <Divider /> */}
            <div className="space-y-2 px-3 pr-1.5">
                <SidebarItemSkeleton />
                <SidebarItemSkeleton />
                <SidebarItemSkeleton />
            </div>
            {/* <Divider /> */}
            <div className="space-y-2 px-3 pr-1.5">
                <SidebarItemSkeleton />
                <SidebarItemSkeleton />
                <SidebarItemSkeleton />
            </div>
        </div>
    );
}
